import React, { useRef, useState, useEffect } from 'react';

interface VideoPlayerProps {
  fileId: string;
  filename?: string;
  onError?: (error: string) => void;
}

export default function VideoPlayer({ fileId, filename = 'Video', onError }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/booking/stream/${fileId}`;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setLoading(true);
    const handleCanPlay = () => setLoading(false);
    const handleError = () => {
      const errorMsg = 'Failed to load video. Please try again.';
      setError(errorMsg);
      setLoading(false);
      onError?.(errorMsg);
    };
    
    // Add event listeners
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    
    // Cleanup
    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [fileId, onError]);

  // Reset video when fileId changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset video state when fileId changes
    setError(null);
    setLoading(true);
    
    // Force reload the video source
    video.load();
  }, [fileId]);

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#ef4444',
        background: '#fef2f2',
        borderRadius: '8px',
        border: '1px solid #fecaca'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: '500' }}>
          ⚠️ Video Loading Error
        </div>
        <div style={{ fontSize: '14px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          Loading video...
        </div>
      )}
      
      <video
        ref={videoRef}
        controls
        preload="metadata"
        controlsList="nodownload"
        disablePictureInPicture
        style={{ 
          width: '100%', 
          height: 'auto',
          borderRadius: '8px',
          backgroundColor: '#000'
        }}
        title={filename}
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={streamUrl} type="video/mp4" />
        <p style={{ padding: '20px', textAlign: 'center' }}>
          Your browser does not support the video tag.
        </p>
      </video>
    </div>
  );
}
